/**
 * Mail-Vorschau.
 *
 * Zeigt die versendete Erinnerung so, wie sie beim Kunden angekommen ist:
 * Von/An, Betreff, Text und die gerenderte HTML-Signatur des Versenders.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { Modal, KnopfSekundaer } from '../komponenten/Modal';
import type { Angebot, MailVorschau } from '../typen';

export function MailVorschauModal({
  angebot,
  onSchliessen,
}: {
  angebot: Angebot;
  onSchliessen: () => void;
}) {
  const abfrage = useQuery({
    queryKey: ['mail-vorschau', angebot.id],
    queryFn: () => api.hole<MailVorschau>(`/angebote/${angebot.id}/mail-vorschau`),
  });

  const erinnerung = angebot.erinnerungen[0];
  const mail = abfrage.data;

  // Der gespeicherte Text der tatsächlich versendeten Mail hat Vorrang vor der
  // aktuellen Vorlage – sonst zeigt die Vorschau etwas anderes als beim Kunden.
  const betreff = erinnerung?.betreff || mail?.betreff || '';
  const text = erinnerung?.text || mail?.text || '';

  const kopf = erinnerung
    ? `${angebot.name} · ${erinnerung.wochentag}, ${erinnerung.datumText} · ${erinnerung.zeit || '—'}`
    : angebot.name;

  return (
    <Modal
      titel={erinnerung ? `${erinnerung.stufe}. Erinnerung` : 'Erinnerungsmail'}
      untertitel={kopf}
      breite={620}
      zeigeSchliessen
      onSchliessen={onSchliessen}
      fuss={
        <div
          className="modal-fuss--verteilt"
          style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
        >
          <a
            href={erinnerung?.outlookWebLink || mail?.outlookLink || '#'}
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: 13.5, fontWeight: 600 }}
          >
            In Outlook öffnen ↗
          </a>
          <KnopfSekundaer onClick={onSchliessen}>Schließen</KnopfSekundaer>
        </div>
      }
    >
      {abfrage.isLoading && <div className="modal-hinweis">Vorschau wird geladen …</div>}

      {mail && (
        <div className="mail-blatt">
          <div className="mail-kopfzeile">
            <span>Von</span>
            <span>
              {mail.versender} · {mail.versenderEmail || 'keine Adresse hinterlegt'}
              {angebot.versAbweichend ? ` (Kalkulant: ${angebot.kalkulant})` : ''}
            </span>
          </div>
          <div className="mail-kopfzeile">
            <span>An</span>
            <span>
              {mail.empfaengerEmail || '—'} · {mail.empfaengerName}
            </span>
          </div>

          <div className="mail-betreff">{betreff}</div>
          <div className="mail-text">{text}</div>

          <div className="mail-signatur">
            {mail.signaturHtml ? (
              // Die Signatur ist bewusst gepflegtes HTML aus dem Admin Center.
              <div dangerouslySetInnerHTML={{ __html: mail.signaturHtml }} />
            ) : (
              <span className="mail-signatur-leer">
                Keine Signatur für {mail.versender || '—'} hinterlegt.
              </span>
            )}
          </div>
        </div>
      )}

      {erinnerung?.trockenlauf && (
        <div className="eh-banner eh-banner--gelb">
          Testbetrieb: diese Mail wurde als Entwurf im Postfach abgelegt, aber nicht an den Kunden
          versendet.
        </div>
      )}
    </Modal>
  );
}
